export type PublicPixel = {
  name: string;
  provider: string;
  pixelId: string;
  config?: unknown;
};

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const SCRIPT_ID = 'dz-ga4-gtag';
let activeMeasurementId: string | null = null;

function ensureGtag() {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    // GA expects the Arguments object, not a rest-array wrapper.
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
}

/** Load gtag.js and configure GA4. Idempotent for the same measurement ID. */
export function initGa4(measurementId: string) {
  if (typeof document === 'undefined' || !measurementId) return;
  const id = measurementId.trim().toUpperCase();
  if (!/^G-[A-Z0-9]+$/.test(id)) return;

  ensureGtag();

  if (activeMeasurementId === id && document.getElementById(SCRIPT_ID)) {
    return;
  }

  activeMeasurementId = id;
  window.gtag('js', new Date());
  window.gtag('config', id, { send_page_view: false });

  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);
  }
}

export function trackGa4PageView(path: string, title?: string) {
  if (typeof window === 'undefined' || !activeMeasurementId || typeof window.gtag !== 'function') {
    return;
  }
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.href,
  });
}

export function trackGa4Event(name: string, params?: Record<string, unknown>) {
  if (typeof window === 'undefined' || !activeMeasurementId || typeof window.gtag !== 'function') {
    return;
  }
  window.gtag('event', name, params);
}

export function getActiveGa4Id() {
  return activeMeasurementId;
}

export function findGa4Pixel(pixels: PublicPixel[]): PublicPixel | undefined {
  return pixels.find((p) => p.provider.toLowerCase() === 'ga4' && Boolean(p.pixelId));
}
